import { memo, useMemo } from 'react';
import PurchaseKingSection, {
  type IPurchaseKingRank,
} from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/PurchaseKingSection';
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import type {
  PromotionTopBuyer,
  PromotionTopBuyerModule as PromotionTopBuyerModuleData,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import { requestGetPromotionTopBuyer } from '@apis/renewal/promotion';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { useWckQuery } from '@hooks/wckQuery/useWckQuery';
import useModalManager from '@hooks/useModalManager';

const purchaseMockData = [
  { rank: 1, memberName: '김서준', amount: 9847200 },
  { rank: 2, memberName: '이하윤', amount: 8930500 },
  { rank: 3, memberName: '박지우', amount: 8215400 },
  { rank: 4, memberName: '최민서', amount: 7604800 },
  { rank: 5, memberName: '정예린', amount: 7073200 },
  { rank: 6, memberName: '강도현', amount: 6415500 },
  { rank: 7, memberName: '조은채', amount: 5862900 },
  { rank: 8, memberName: '윤시현', amount: 5348300 },
  { rank: 9, memberName: '임수아', amount: 4873100 },
  { rank: 10, memberName: '한지호', amount: 4514600 },
  { rank: 11, memberName: '오세영', amount: 4106200 },
  { rank: 12, memberName: '신유진', amount: 3785900 },
  { rank: 13, memberName: '남궁현우', amount: 3412400 },
  { rank: 14, memberName: '배소윤', amount: 3104700 },
  { rank: 15, memberName: '문가온', amount: 2841000 },
  { rank: 16, memberName: '서지안', amount: 2587500 },
  { rank: 17, memberName: '황보라', amount: 2346300 },
  { rank: 18, memberName: '전다인', amount: 2014800 },
  { rank: 19, memberName: '고은', amount: 1795200 },
  { rank: 20, memberName: '유채원', amount: 1538400 },
];

interface TopBuyerModuleProps {
  bgType: string;
  visualTokens: PromotionModuleVisualTokens;
  module: PromotionTopBuyerModuleData;
  promotionId: number;
  isLogin: boolean;
  isDesktop: boolean;
}

function TopBuyerModule({
  bgType,
  visualTokens,
  module,
  promotionId,
  isLogin,
  isDesktop,
}: TopBuyerModuleProps) {
  const { openLoginConfirm } = useModalManager();
  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });
  const { data: topBuyer } = useWckQuery<PromotionTopBuyer | undefined>({
    queryKey: ['promotionTopBuyer', promotionId],
    queryFn: async () =>
      (await requestGetPromotionTopBuyer(promotionId.toString())).data
        ?.topBuyer,
    retry: 1,
    staleTime: 0,
    enabled: isLogin,
  });

  const topCount = Math.max(module.topCount, 0);
  const colorType = customColorsFull ?? presetBgType;
  const rankList = useMemo<IPurchaseKingRank[]>(
    () =>
      (topBuyer?.items ?? []).slice(0, topCount).map(item => ({
        rank: item.rank,
        memberName: item.custName,
        amount: item.orderAmount,
      })),
    [topBuyer?.items, topCount]
  );
  const updateText = topBuyer?.generatedAt
    ? `${dayjs(topBuyer.generatedAt).format(
        'YYYY-MM-DD HH:mm'
      )} 기준 (30분 단위 업데이트)`
    : undefined;

  if (isLogin && !topBuyer) return null;

  return (
    <PurchaseKingSection
      title={`상위 TOP ${topCount}`}
      subTitle={updateText}
      myAmount={topBuyer?.myOrderAmount}
      rankList={isLogin ? rankList : purchaseMockData}
      isLogin={isLogin}
      isDesktop={isDesktop}
      colorType={colorType}
      noDataMessage="실적을 집계하고 있어요. 순위가 완성되면 보여드릴게요."
      onLoginClick={openLoginConfirm}
    />
  );
}

export default memo(TopBuyerModule);
